<template>
    <div class="modal-backdrop" @click.self="$emit('close')">
        <div class="modal-card">
            <div class="modal-header-custom">
                <h2 class="modal-title">{{ title }}</h2>
                <button class="modal-close" type="button" @click="$emit('close')">
                    <i class="fas fa-times"></i>
                </button>
            </div>

            <div class="modal-body-custom">
                <div v-if="items && items.length > 0" class="promotion-list">
                    <div 
                        v-for="(item, index) in items" 
                        :key="item.id || index"
                        class="promotion-item"
                        @click="handleItemClick(item)"
                    >
                        <!-- Image / Thumbnail -->
                        <div class="item-image">
                            <img 
                                v-if="getItemImage(item)" 
                                :src="getItemImage(item)" 
                                :alt="item.name"
                                loading="lazy"
                            >
                            <div v-else class="image-placeholder">
                                {{ getInitials(item) }}
                            </div>
                        </div>

                        <!-- Content Details -->
                        <div class="item-details">
                            <div class="item-header">
                                <h3 class="item-name">{{ item.name }}</h3>
                                <!-- Status Badge for Locked Items -->
                                <span v-if="item.isLocked" class="status-badge locked">
                                    <i class="fas fa-lock"></i> Locked
                                </span>
                            </div>
                            
                            <p class="item-merchant">
                                <i class="fas fa-store"></i> {{ item.merchant?.name || item.merchant_name || 'Merchant' }}
                            </p>
                            
                            <!-- Additional Info Row -->
                            <div class="item-info-row">
                                <!-- Points / Cost -->
                                <span v-if="item.point" class="info-tag points">
                                    {{ item.point }} Pts
                                </span>

                                <!-- Stock / Availability -->
                                <span v-if="item.item_type === 'voucher'" class="info-tag stock" :class="{ 'unlimited': isUnlimited(item) }">
                                    <i class="fas fa-box"></i>
                                    {{ isUnlimited(item) ? 'Unlimited' : (item.stock || 0) + ' left' }}
                                </span>

                                <!-- Benefit Tag -->
                                <span v-if="item.item_type === 'benefit'" class="info-tag benefit">
                                    Benefit
                                </span>
                            </div>
                        </div>

                        <!-- Action / Chevron -->
                        <div class="item-action">
                            <i class="fas fa-chevron-right"></i>
                        </div>
                    </div>
                </div>
                <div v-else class="empty-state">
                    <img src="/img/member-icon/voucher-logo.svg" alt="Empty" width="60" style="opacity: 0.5; margin-bottom: 12px;">
                    <p>No promotions available at the moment.</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AllPromotionsModal',
    props: {
        items: {
            type: Array,
            default: () => []
        },
        title: {
            type: String,
            default: 'All Promotions'
        }
    },
    emits: ['close', 'click-item'],
    mounted() {
        document.body.style.overflow = 'hidden';
    },
    beforeUnmount() {
        document.body.style.overflow = '';
    },
    methods: {
        handleItemClick(item) {
            this.$emit('click-item', item);
        },
        getItemImage(item) {
            // Priority: item avatar -> merchant logo -> corporate logo -> null (placeholder)
            return item.avatar?.image_url || 
                   item.image_url || 
                   item.merchant?.logo?.image_url || 
                   null;
        },
        getInitials(item) {
            const name = item.merchant?.name || item.merchant_name || item.name || '';
            if (!name) return '?';
            return name.slice(0, 2).toUpperCase();
        },
        isUnlimited(item) {
            return item.stock === -1 || item.stock === null;
        }
    }
}
</script>

<style scoped>
.modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(17, 24, 39, 0.65); /* Slightly darker for better focus */
    backdrop-filter: blur(2px); /* Modern touch */
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px; 
    z-index: 900; /* Lower than detail modal (1000) */
}

/* Modal Card Animation */
@keyframes modalSlideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

.modal-card {
    position: relative;
    width: 600px;
    max-width: 100%;
    height: 80vh; /* Fixed height for better list scrolling */
    background: #ffffff;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    animation: modalSlideUp 0.3s ease-out;
}

/* Modal Header */
.modal-header-custom {
    padding: 24px 24px 16px;
    border-bottom: 1px solid #E2E8F0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: white;
}

.modal-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #0F172A;
    /* Ensure title wraps if long */
    white-space: normal;
    word-break: break-word;
    line-height: 1.4;
    padding-right: 12px;
}

.modal-close {
    background: transparent;
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    color: #64748B;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.modal-close:hover {
    background: #F1F5F9;
    color: #0F172A;
}

.modal-body-custom {
    overflow-y: auto;
    padding: 0; /* Clean list look */
    flex: 1;
    background: #F8FAFC;
}

/* List Styling */
.promotion-list {
    display: flex;
    flex-direction: column;
}

.promotion-item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    background: white;
    border-bottom: 1px solid #F1F5F9;
    cursor: pointer;
    transition: background 0.2s;
}

.promotion-item:last-child {
    border-bottom: none;
}

.promotion-item:active {
    background: #F1F5F9;
}

@media(hover: hover) {
    .promotion-item:hover {
        background: #F8FAFC;
    }
}

/* Image */
.item-image {
    width: 64px;
    height: 64px;
    border-radius: 12px;
    overflow: hidden;
    flex-shrink: 0;
    border: 1px solid #E2E8F0;
    background: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
}

.item-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.image-placeholder {
    width: 100%;
    height: 100%;
    background: var(--primary-gradient, linear-gradient(135deg, #F93E3E 0%, #FDA4AF 100%));
    color: white;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
}

/* Details */
.item-details {
    flex: 1;
    min-width: 0; /* Text truncation fix */
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.item-header {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.item-name {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #0F172A;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.item-merchant {
    margin: 0;
    font-size: 13px;
    color: #64748B;
    display: flex;
    align-items: center;
    gap: 6px;
}

.item-merchant i {
    font-size: 11px;
    opacity: 0.7;
}

.item-info-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
    flex-wrap: wrap;
}

/* Tags */
.info-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
}

.info-tag.points {
    background: #EFF6FF;
    color: #3B82F6;
}

.info-tag.stock {
    background: #FFF7ED;
    color: #EA580C;
}

.info-tag.stock.unlimited {
    background: #F0FDF4;
    color: #16A34A;
}

.info-tag.benefit {
    background: #ECFDF5;
    color: #059669;
}

/* Locked Badge */
.status-badge.locked {
    font-size: 10px;
    background: #F1F5F9;
    color: #64748B;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 3px;
}

/* Action */
.item-action {
    color: #CBD5E1;
    font-size: 14px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #94A3B8;
    padding: 32px;
    text-align: center;
}

/* Mobile Responsiveness */
@media (max-width: 640px) {
    .modal-backdrop {
        padding: 0;
        align-items: flex-end; /* Bottom sheet feel */
    }
    
    .modal-card {
        width: 100%;
        height: 85vh; /* Taller on mobile */
        border-radius: 20px 20px 0 0;
        max-width: none;
    }

    .modal-header-custom {
        padding: 20px;
    }
    
    .modal-body-custom {
        padding-bottom: 90px; /* Space for mobile bottom navigation */
    }
    
    .item-image {
        width: 56px;
        height: 56px;
    }
}
</style>
